import { useId, useState } from 'react';
import type { PayerHistoryItem, PayerHistoryResponse } from '@features/patient-coverages/types';
import { Chip } from '@jotpsych/ui';
import { CloseRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import { ELIG_TOKENS } from '../theme/readinessTokens';
import { payerHistorySummary } from '../utils/payerHistorySummary';

interface Props {
  data?: PayerHistoryResponse;
  loading?: boolean;
  error?: boolean;
  denied?: boolean;
  coverageLabel?: string;
  onRefresh: () => void;
  onClaimOpen: () => void;
  onHistoryOpen?: () => void;
}

const statusLabels: Record<PayerHistoryItem['lifecycle_status'], string> = {
  reported: 'Reported',
  corrected: 'Corrected',
  superseded: 'Superseded',
  reversed: 'Reversed',
  pending_secondary: 'Pending secondary',
  unresolved: 'Unresolved',
};

const reasonLabels: Record<NonNullable<PayerHistoryItem['lifecycle_reasons']>[number], string> = {
  no_adjudication: 'No payer adjudication available',
  multiple_adjudications: 'Multiple payer reports need review',
  source_limit: 'Additional payer reports omitted',
  line_limit: 'Additional service lines omitted',
  malformed_amount: 'A reported amount could not be read',
  missing_source: 'Source details unavailable',
  unknown_status: 'Source status could not be determined',
  correction: 'Corrected claim',
  superseded: 'Claim replaced by a later claim',
  reversal: 'Report reversed',
  secondary_pending: 'Awaiting secondary payer adjudication',
  missing_correction_link: 'Related correction could not be identified',
  missing_service_date: 'Service date unavailable',
  duplicate_source: 'Duplicate report received',
  ambiguous_components: 'Reported components could not be separated',
  secondary_forwarded: 'Forwarded to secondary; later outcome unknown',
  malformed_date: 'A reported date could not be read',
};

function money(cents: number | null | undefined): string {
  return cents == null
    ? 'Unknown'
    : (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
}

function date(value: string | null | undefined): string {
  return value && dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY') : 'Unknown';
}

function ClaimLink({ item, onClaimOpen }: { item: PayerHistoryItem; onClaimOpen: () => void }) {
  return (
    <Link
      href={`/jotbill/claim/${encodeURIComponent(item.claim_id)}`}
      onClick={onClaimOpen}
      aria-label={`Open claim ${item.claim_id}`}
      sx={{ fontSize: 'inherit' }}
    >
      Claim
    </Link>
  );
}

/** A compact past-visit summary with every returned source available in the dialog. */
export function PayerHistoryPanel({
  data,
  loading,
  error,
  denied,
  coverageLabel,
  onRefresh,
  onClaimOpen,
  onHistoryOpen,
}: Props) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  // Suppress provided/cached sources in both surfaces during access revalidation.
  const safeData = loading || error || denied ? undefined : data;
  const summary = payerHistorySummary(safeData?.items ?? []);
  const item = summary.item;
  const message = denied
    ? 'Claim-read access is required to view payer history.'
    : error
      ? 'Could not load payer history. Refresh to try again.'
      : loading || !data
        ? 'Loading payer history…'
        : undefined;
  return (
    <Box
      component="section"
      aria-label="Payer responsibility history"
      sx={{
        border: '1px solid',
        borderColor: ELIG_TOKENS.lineStrong,
        borderRadius: 1.5,
        minWidth: 0,
        flex: 1,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Typography sx={{ color: ELIG_TOKENS.inkFaint, fontSize: 10.5, fontWeight: 650 }}>
        Last payer-reported responsibility
      </Typography>
      {message ? (
        <Typography role="status" sx={{ fontSize: 11.5, mt: 0.5 }}>
          {message}
        </Typography>
      ) : (
        <>
          <Typography sx={{ color: ELIG_TOKENS.ink, fontSize: 14, fontWeight: 750, mt: 0.25 }}>
            {item ? money(item.reported_responsibility_cents) : 'Unavailable'}
          </Typography>
          <Typography
            component="div"
            sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 10.5, lineHeight: 1.4, mt: 0.5 }}
          >
            {item ? (
              <>
                Visit {date(item.service_date_from)} ·{' '}
                <ClaimLink item={item} onClaimOpen={onClaimOpen} />
              </>
            ) : safeData?.truncated ? (
              'No usable amount in returned history.'
            ) : safeData?.items.length ? (
              'No dated, usable payer amount.'
            ) : (
              'No payer reports for this policy.'
            )}
          </Typography>
          {summary.hasNewerUnusable && (
            <Typography sx={{ color: ELIG_TOKENS.amber, fontSize: 10.5, mt: 0.5 }}>
              More recent history is pending or unavailable.
            </Typography>
          )}
          {safeData?.truncated && (
            <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 10.5, mt: 0.5 }}>
              Limited history
            </Typography>
          )}
        </>
      )}
      <Button
        size="small"
        onClick={() => {
          onHistoryOpen?.();
          setOpen(true);
        }}
        aria-haspopup="dialog"
        sx={{ px: 0, mt: 0.5, minWidth: 0, fontSize: 11 }}
      >
        View payer history
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        slotProps={{
          paper: {
            sx: {
              m: { xs: 1.5, sm: 4 },
              width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
              maxHeight: 'calc(100% - 48px)',
            },
          },
        }}
      >
        <DialogTitle id={`${titleId}-heading`} sx={{ pr: 7, fontSize: 18, fontWeight: 750 }}>
          <span id={titleId}>Payer history</span>
          <IconButton
            aria-label="Close payer history"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minWidth: 0 }}>
          <Typography
            id={descriptionId}
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, overflowWrap: 'anywhere' }}
          >
            {coverageLabel ? `${coverageLabel} · ` : ''}This exact policy only. Past reported
            amounts do not set current benefits or a final balance.
          </Typography>
          {message ? (
            <Alert severity={error && !denied ? 'error' : 'info'}>{message}</Alert>
          ) : (
            <>
              {safeData?.truncated && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This is a limited history. Older claims or additional source details may be
                  omitted.
                </Alert>
              )}
              {safeData?.items.length ? (
                <TableContainer
                  tabIndex={0}
                  role="region"
                  aria-label="Payer history table; scroll for more columns and reports"
                  sx={{
                    maxHeight: '55vh',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table
                    stickyHeader
                    size="small"
                    aria-label="Payer history"
                    sx={{ minWidth: 1050 }}
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          'Service date',
                          'Source date',
                          'Payer / claim',
                          'Responsibility',
                          'Deductible',
                          'Coinsurance',
                          'Copay',
                          'Status / details',
                        ].map(label => (
                          <TableCell key={label} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {safeData.items.map((report, index) => (
                        <TableRow
                          key={`${report.claim_id}/${report.remittance_claim_id ?? 'claim'}/${index}`}
                          sx={{ '& td': { verticalAlign: 'top' } }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {date(report.service_date_from)}
                            {report.service_date_to &&
                              report.service_date_to !== report.service_date_from && (
                                <>
                                  <br />– {date(report.service_date_to)}
                                </>
                              )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {date(report.source_date)}
                            {report.recorded_at && (
                              <Typography variant="caption" component="div" color="text.secondary">
                                Recorded {date(report.recorded_at)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ minWidth: 145, maxWidth: 210, overflowWrap: 'anywhere' }}
                          >
                            <Typography variant="body2">
                              {report.payer_name ??
                                report.submitted_policy.payer_name ??
                                'Unknown payer'}
                            </Typography>
                            <ClaimLink item={report} onClaimOpen={onClaimOpen} />
                          </TableCell>
                          {[
                            report.reported_responsibility_cents,
                            report.deductible_cents,
                            report.coinsurance_cents,
                            report.copay_cents,
                          ].map((amount, amountIndex) => (
                            <TableCell key={amountIndex} sx={{ whiteSpace: 'nowrap' }}>
                              {money(amount)}
                            </TableCell>
                          ))}
                          <TableCell sx={{ minWidth: 200 }}>
                            <Chip
                              label={statusLabels[report.lifecycle_status]}
                              size="small"
                              variant="soft"
                              color={
                                report.lifecycle_status === 'unresolved' ||
                                report.lifecycle_status === 'pending_secondary'
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                            {!!report.lifecycle_reasons?.length && (
                              <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {report.lifecycle_reasons
                                  .map(reason => reasonLabels[reason])
                                  .join(' · ')}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2">
                  {safeData?.truncated
                    ? 'No payer reports in this returned history.'
                    : 'No payer history linked to this exact policy.'}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!denied && (
            <Button onClick={onRefresh} disabled={loading}>
              Refresh history
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
